/**
 * Rhythmz — Scene: Mechanical Beast
 * ──────────────────────────────────
 * A cybernetic war-machine — part insect, part tank. Dark metallic body with
 * hot orange emissive seams, segmented legs that walk on beat, pistons that
 * pump with bass, and LED eyes that flash.
 *
 * Audio: Bass → piston depth + leg stride. Beat → head snap + LED flash.
 */

import * as THREE from 'three/webgpu';
import { Fn, vec4, float, fract, add, mul, pow, positionLocal } from 'three/tsl';
import { SceneLighting, type AudioRef } from './SceneLighting';
import type { SceneHandle } from './SceneManager';

type N = any;

export interface BeastUniforms {
  uTime: N; uBass: N; uMid: N; uBeat: N; uTreble: N;
  uCol0: N; uCol1: N; uCol2: N;
}

export function createMechanicalBeast(u: BeastUniforms): SceneHandle {
  const group = new THREE.Group();
  const lighting = new SceneLighting({
    keyColor: new THREE.Color(0.6, 0.7, 0.9),   // cool industrial white
    keyIntensity: 12,
    keyPosition: new THREE.Vector3(4, 6, 5),
    fillColor: new THREE.Color(1.0, 0.4, 0.1),   // warm orange fill
    fillIntensity: 5,
    fillPosition: new THREE.Vector3(-3, -2, 4),
    rimColor: new THREE.Color(0.1, 0.3, 0.8),    // deep blue rim
    rimIntensity: 6,
    rimPosition: new THREE.Vector3(0, -3, -6),
    camLightIntensity: 2,
  });
  group.add(lighting.group);

  // ── Metallic material with emissive seams ──
  const metalMat = new THREE.MeshStandardNodeMaterial({
    metalness: 0.9,
    roughness: 0.3,
    color: 0x1a1a25,
  });

  // Emissive seams: hot orange glow that pulses with bass
  metalMat.emissiveNode = Fn(() => {
    const seam = pow(fract(mul(positionLocal.y, float(5.0))), float(8.0));
    const glow = mul(seam, add(float(0.3), mul(u.uBass, float(0.8))));
    return vec4(mul(float(1.0), glow), mul(float(0.4), glow), mul(float(0.05), glow), float(1.0));
  })();

  // ── Body: capsule ──
  const bodyGeo = new THREE.CapsuleGeometry(0.7, 1.2, 8, 16);
  const body = new THREE.Mesh(bodyGeo, metalMat);
  body.position.set(0, 0, 0);
  body.rotation.z = Math.PI / 2; // horizontal
  group.add(body);

  // ── Head: box with beveled look (use a smaller box) ──
  const headGeo = new THREE.BoxGeometry(0.6, 0.5, 0.7);
  const head = new THREE.Mesh(headGeo, metalMat);
  head.position.set(1.3, 0.3, 0);
  group.add(head);

  // ── LED Eyes: two emissive spheres ──
  const eyeGeo = new THREE.SphereGeometry(0.08, 8, 8);
  const eyeMat = new THREE.MeshStandardNodeMaterial({
    metalness: 0.0,
    roughness: 0.0,
  });
  eyeMat.emissiveNode = Fn(() => {
    const flash = add(float(0.5), mul(u.uBeat, float(2.0)));
    return vec4(mul(float(1.0), flash), mul(float(0.1), flash), mul(float(0.1), flash), float(1.0));
  })();

  const eyeL = new THREE.Mesh(eyeGeo, eyeMat);
  eyeL.position.set(1.55, 0.4, 0.18);
  group.add(eyeL);
  const eyeR = new THREE.Mesh(eyeGeo, eyeMat);
  eyeR.position.set(1.55, 0.4, -0.18);
  group.add(eyeR);

  // ── Spine spikes: cone instances ──
  const spikeCount = 6;
  const spikeGeo = new THREE.ConeGeometry(0.12, 0.4, 6);
  const spikes = new THREE.InstancedMesh(spikeGeo, metalMat, spikeCount);
  spikes.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(spikes);

  const spikePos: { x: number; y: number; z: number }[] = [];
  for (let i = 0; i < spikeCount; i++) {
    spikePos.push({ x: -0.8 + i * 0.35, y: 0.65, z: 0 });
  }

  // ── Legs: 6 segmented cylinders (InstancedMesh) ──
  const legSegCount = 18; // 6 legs × 3 segments
  const legGeo = new THREE.CylinderGeometry(0.06, 0.08, 0.5, 6);
  const legs = new THREE.InstancedMesh(legGeo, metalMat, legSegCount);
  legs.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(legs);

  // Leg config: 6 legs, 3 segments each, alternating sides
  const legConfigs: { side: number; zOff: number; segIdx: number }[] = [];
  for (let leg = 0; leg < 6; leg++) {
    const side = leg < 3 ? 1 : -1;
    const zOff = (leg % 3 - 1) * 0.4;
    for (let seg = 0; seg < 3; seg++) {
      legConfigs.push({ side, zOff, segIdx: seg });
    }
  }

  // ── Pistons: 4 cylinders that extend/retract ──
  const pistonCount = 4;
  const pistonGeo = new THREE.CylinderGeometry(0.1, 0.1, 1, 8);
  const pistons = new THREE.InstancedMesh(pistonGeo, metalMat, pistonCount);
  pistons.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  group.add(pistons);

  const pistonPos: { x: number; y: number; z: number }[] = [
    { x: -0.4, y: 0, z: 0.5 },
    { x: 0.4, y: 0, z: 0.5 },
    { x: -0.4, y: 0, z: -0.5 },
    { x: 0.4, y: 0, z: -0.5 },
  ];

  // ── Animation ──
  let t = 0;
  const pivot = new THREE.Object3D();

  const update = (dt: number): void => {
    t += dt;
    const audio: AudioRef = {
      bass: u.uBass.value ?? 0,
      beat: u.uBeat.value ?? 0,
      mid: u.uMid.value ?? 0,
      level: ((u.uBass.value ?? 0) + (u.uMid.value ?? 0)) * 0.5,
    };
    lighting.update(audio);

    // Body rotation — slow menacing turn
    group.rotation.y = Math.sin(t * 0.15) * 0.4;
    group.rotation.x = Math.sin(t * 0.1) * 0.05;

    // Head tracking — swivels on beat
    head.rotation.y = Math.sin(t * 0.8) * 0.3 + (u.uBeat.value ?? 0) * 0.2;
    head.position.y = 0.3 + Math.sin(t * 1.2) * 0.05;

    // Spikes extend on drop
    for (let i = 0; i < spikeCount; i++) {
      const sp = spikePos[i];
      const extend = 1 + (u.uBass.value ?? 0) * 0.5;
      pivot.position.set(sp.x, sp.y + 0.1, sp.z);
      pivot.rotation.set(0, 0, 0);
      pivot.scale.set(1, extend, 1);
      pivot.updateMatrix();
      spikes.setMatrixAt(i, pivot.matrix);
    }
    spikes.instanceMatrix.needsUpdate = true;

    // Legs walk cycle — bass drives stride
    const stride = (u.uBass.value ?? 0) * 0.5 + 0.1;
    for (let i = 0; i < legSegCount; i++) {
      const cfg = legConfigs[i];
      const phase = t * 2 + cfg.zOff * 3 + cfg.segIdx * 0.5;
      const lift = Math.sin(phase) * 0.1 * stride;
      const x = -0.6 + cfg.segIdx * 0.25;
      pivot.position.set(x, -0.3 - cfg.segIdx * 0.2 + lift, cfg.side * (0.5 + cfg.segIdx * 0.15));
      pivot.rotation.set(Math.sin(phase) * 0.3, 0, cfg.side * 0.4);
      pivot.scale.set(1, 1, 1);
      pivot.updateMatrix();
      legs.setMatrixAt(i, pivot.matrix);
    }
    legs.instanceMatrix.needsUpdate = true;

    // Pistons pump — bass drives extension
    for (let i = 0; i < pistonCount; i++) {
      const pp = pistonPos[i];
      const pump = 0.5 + Math.sin(t * 4 + i * 1.5) * 0.2 + (u.uBass.value ?? 0) * 0.3;
      pivot.position.set(pp.x, pp.y, pp.z);
      pivot.rotation.set(0, 0, 0);
      pivot.scale.set(1, pump, 1);
      pivot.updateMatrix();
      pistons.setMatrixAt(i, pivot.matrix);
    }
    pistons.instanceMatrix.needsUpdate = true;
  };

  const dispose = (): void => {
    lighting.dispose();
    bodyGeo.dispose();
    headGeo.dispose();
    eyeGeo.dispose(); eyeMat.dispose();
    spikeGeo.dispose();
    legGeo.dispose();
    pistonGeo.dispose();
    metalMat.dispose();
    group.clear();
  };

  return { group, update, dispose, name: 'Mechanical Beast' };
}